<template>
  <v-container fluid :class="mobile ? 'pa-2' : 'pa-4'" class="fill-height align-start">
    <v-row class="justify-center">
      <v-col cols="12" sm="10" md="8">
        <h2 class="text-h5 text-center mb-6 mt-4">Welcome to Pizza Felice</h2>

        <!-- One block per sidebar section, in sidebar order. Each card is a
             sidebar group (or standalone entry); its children are the chips. -->
        <template v-for="(section, si) in sections" :key="section.key">
          <v-divider v-if="si > 0" class="my-6" />
          <h3 class="text-subtitle-1 text-medium-emphasis text-uppercase mb-4">{{ section.title }}</h3>
          <v-row>
            <v-col v-for="entry in section.entries" :key="entry.key" cols="12" sm="6" class="d-flex">
              <v-card
                class="service-card flex-grow-1"
                variant="outlined"
                :hover="!!entry.path"
                @click="entry.path ? go(entry) : null"
              >
                <v-card-item>
                  <template v-slot:prepend>
                    <v-icon :icon="entry.icon" size="32" :color="entry.iconColor || 'indigo'" />
                  </template>
                  <v-card-title>{{ entry.title }}</v-card-title>
                  <v-card-subtitle v-if="entry.path">Open {{ entry.landing || entry.title }}</v-card-subtitle>
                </v-card-item>
                <v-card-text v-if="entry.children.length" class="pt-0">
                  <v-chip
                    v-for="child in entry.children"
                    :key="child.title"
                    size="small"
                    variant="tonal"
                    class="me-1 mb-1"
                    :prepend-icon="child.icon"
                    @click.stop="go(child)"
                  >
                    {{ child.title }}
                  </v-chip>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </template>

        <!-- App admins see everything, so the line is for everybody else (Maurizio, 2026-10-09) -->
        <p v-if="!gateCtx.isAppAdmin" class="text-caption text-medium-emphasis text-center mt-6">
          You only see what you have been granted access to.
        </p>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
/**
 * Home — the sidebar, laid out as cards.
 *
 * Built from the SAME config as the sidebar (config/navGroups.js), section by
 * section, so the two can never drift (Maurizio, 2026-09-09: "reorganize the
 * home page according to the sidebar menu reorganization"). Before this the
 * page kept its own hand-written list of six "services" with its own copy of
 * the allowed_apps parsing — the fourth such copy in the codebase.
 *
 * Reading: a card per group (or standalone entry) under its section label;
 * the group's visible children are chips. A card opens the group's page
 * (Warehouse → Inventory, GL → Journal, Sales → Analytics …); an accordion
 * group's card (Daily/Monthly operations) opens its LEAD entry — the first
 * child this user can see (Delivery, or Stock Out for a kitchen operator) —
 * the same rule as its sidebar header (2026-09-11). System Settings appears
 * as the last section for appadmin.
 */
import { computed } from "vue";
import { useRouter } from "vue-router";
import { useDisplay } from "vuetify";
import { useApp } from "@/composables/useApp.js";
import { navGroups, settingsGroup, isEntryVisible, isGroupVisible } from "@/config/navGroups.js";

const router = useRouter();
const { mobile } = useDisplay();
const { user, currentApp, allowedApps } = useApp();

// Same visibility rules as the sidebar, same helpers.
const gateCtx = computed(() => ({ allowedApps: allowedApps.value, isAppAdmin: user.value?.role === "appadmin" }));
const visibleChildren = (group) => (group.children || []).filter((c) => isEntryVisible(c, gateCtx.value));
const asEntry = (item) => {
  const children = item.type === "group" ? visibleChildren(item) : [];
  // A path-less accordion (Daily / Monthly operations) opens its LEAD entry —
  // the first child this user can see — exactly like its sidebar header does
  // since 2026-09-11 (Maurizio: "make the Home page cards for Daily and
  // Monthly clickable"). The card's "Open …" line names that entry.
  const lead = item.type === "group" && !item.path ? children[0] || null : null;
  return {
    key: item.key,
    title: item.title,
    icon: item.icon,
    iconColor: item.iconColor,
    path: item.path || lead?.path || null,
    landing: lead ? lead.title : null,
    currentApp: lead ? lead.currentApp : item.currentApp,
    children,
  };
};

/**
 * Walk navGroups in order: a "subheader" starts a section; every visible
 * group/leaf after it belongs to that section. Entries before the first label
 * (Home itself) are skipped — a Home tile on the Home page says nothing.
 */
const sections = computed(() => {
  const ctx = gateCtx.value;
  const out = [];
  let current = null;
  for (const item of navGroups) {
    if (item.type === "divider") continue; // a rule in the drawer only — nothing to show on Home
    if (item.type === "subheader") {
      current = { key: item.key, title: item.title, entries: [] };
      out.push(current);
      continue;
    }
    if (!current) continue;
    const visible = item.type === "group" ? isGroupVisible(item, ctx) : isEntryVisible(item, ctx);
    if (visible) current.entries.push(asEntry(item));
  }
  // System Settings joins the Administration section (with Users, Audit Logs,
  // Documentation) rather than forming a second block of the same name.
  if (ctx.isAppAdmin) {
    const admin = out.find((s) => s.key === "sh-admin");
    if (admin) admin.entries.push(asEntry(settingsGroup));
    else out.push({ key: "sh-admin", title: "Administration", entries: [asEntry(settingsGroup)] });
  }
  return out.filter((s) => s.entries.length);
});

// Same navigation side effect as the sidebar's go().
function go(item) {
  if (!item.path) return;
  if (item.currentApp) {
    currentApp.value = item.currentApp;
    localStorage.setItem("currentApp", JSON.stringify(item.currentApp));
  }
  router.push(item.path);
}
</script>

<style scoped>
.service-card {
  transition: border-color 0.2s;
}
.service-card:hover {
  border-color: rgb(var(--v-theme-primary));
}
</style>
